/*
 * Cliente del incremento 6/6 · Panel de vencimientos.
 *
 * Usa la fábrica del núcleo (crearCliente) para NO reimplementar la cookie de
 * sesión, la envoltura común de respuesta ni el aviso de fin de sesión al shell.
 *
 * LO QUE ESTE MÓDULO NO HACE, Y ES CONTRATO (RNF-059):
 *   · No compara fechas, no resta importes, no deduce qué está vencido y no
 *     compone el texto del estado ni el contador de mensualidades.
 *   · No reordena la lista: el orden lo produce el servidor (RN-273, RN-292).
 *   · No manda `diasDeAnticipacion`, `estado` ni `pagada`: el servidor los rechaza
 *     con CAMPO_NO_ADMITIDO (422) y con razón, porque devolverían a la pantalla
 *     decisiones que RNF-038 le quitó.
 */

import { crearCliente, guardarConConexion } from '../../../nucleo/api/cliente.js';

const api = crearCliente('/api/vencimientos');

/** Las cuatro claves de estado. Son valor de lógica; el nombre visible no lo es. */
export const ESTADOS = Object.freeze({
  porVencer: 'POR_VENCER',
  vencida: 'VENCIDA',
  pagada: 'PAGADA',
  sinSaldo: 'SIN_SALDO',
});

function consulta(parametros) {
  const partes = Object.entries(parametros)
    .filter(([, valor]) => valor !== undefined && valor !== null && valor !== '')
    .map(([clave, valor]) => `${clave}=${encodeURIComponent(valor)}`);

  return partes.length > 0 ? `?${partes.join('&')}` : '';
}

/**
 * §1 · ConsultarPanelDeVencimientos (RF-112 a RF-124).
 *
 * ES LA FUENTE ÚNICA del panel y del bloque del Tablero: las dos pantallas
 * consumen ESTA operación y muestran exactamente las mismas cifras. Si mostraran
 * importes o estados distintos para la misma tarjeta, es defecto (RN-278).
 *
 * `fechaLocal` es la del dispositivo y el servidor NUNCA la suple ni la deriva de
 * UTC (D-06, D-184, RNF-057): el estado entero es una comparación de fechas y un
 * día de diferencia convierte «por vencer» en «vencida».
 *
 * `incluirSinSaldo` es de CONSUMO INTERNO y NO tiene control visible en ninguna
 * pantalla (D-192, RN-290): la pestaña manda `true` —u omite— y el bloque del
 * Tablero manda `false`. Con uno u otro valor los totales son IDÉNTICOS (RN-290a).
 */
export function consultarPanel({ fechaLocal, idCuenta, idUsuarioPropietario, incluirSinSaldo } = {}) {
  return api.obtener(consulta({ fechaLocal, idCuenta, idUsuarioPropietario, incluirSinSaldo }));
}

/** §2 · ConsultarConfiguracionDeVencimientos (RF-117). Sin entradas: el usuario sale de la cookie. */
export function consultarConfiguracion() {
  return api.obtener('/configuracion');
}

/**
 * §3 · GuardarConfiguracionDeVencimientos (RF-117, RN-264).
 *
 * Se puede cambiar EN CUALQUIER MOMENTO y surte efecto en la consulta siguiente,
 * sin recargar la aplicación y sin proceso alguno. Los límites 0 y 30 los da el
 * servidor: la pantalla NO los lleva escritos (RN-261).
 */
export function guardarConfiguracion(diasDeAnticipacion) {
  return guardarConConexion(() => api.actualizar('/configuracion', { diasDeAnticipacion }));
}

// =============================================================================
// INCREMENTO 19/21 · RECONCILIACIÓN DEL DIFERIDO DECLARADO (§5 y §5 bis)
//
// LA CONDICIÓN EXPRESA DE Deisy Paez: la consulta REPORTA y NO AJUSTA NADA. La
// única vía de ajuste es `descontarRespaldo`, que SIEMPRE la dispara ella desde
// el botón, tras una confirmación que le enseña la cifra de antes y la de
// después. Ninguna consulta de este módulo la invoca.
// =============================================================================

/** Los tres resultados de la comparación (RN-721). Son valor de lógica. */
export const ESTADOS_DE_RECONCILIACION = Object.freeze({
  enOrden: 'EN_ORDEN',
  declaradoSinPlan: 'DECLARADO_SIN_PLAN',
  respaldoSinDescontar: 'RESPALDO_SIN_DESCONTAR',
});

/**
 * §5 · ConsultarReconciliacionDeDiferido (RF-259). SOLO LECTURA: abrirla no
 * cambia ninguna cifra. Sin `idCuenta` compara todas las tarjetas; con
 * `idCuenta`, una. Los textos de cada resultado los arma el servidor (RN-733):
 * la pantalla NO los compone y NO los varía.
 */
export function consultarReconciliacionDeDiferido({ fechaLocal, idCuenta } = {}) {
  return api.obtener(`/reconciliacion-de-diferido${consulta({ fechaLocal, idCuenta })}`);
}

/**
 * §5 bis · DescontarRespaldoDeDiferidoDeclarado (RF-263, D-419).
 *
 * LOS DOS VALORES OBSERVADOS VIAJAN SIEMPRE y no son adorno: son los que Deisy
 * Paez vio en el reporte y confirmó. Si alguno se movió entre tanto, el
 * servidor responde sin escribir nada y la pantalla vuelve a consultar
 * (RN-727d). UNA TARJETA POR DISPARO (RN-727a): no hay «descontar todas».
 */
export function descontarRespaldo({
  idCuenta, importeDiferidoDeclaradoObservado, importeRespaldoSinDescontarObservado, fechaLocal,
}) {
  return api.publicar('/reconciliacion-de-diferido/descontar-respaldo', {
    idCuenta, importeDiferidoDeclaradoObservado, importeRespaldoSinDescontarObservado, fechaLocal,
  });
}
